import {
	REQUEST_PROJECTS,
	REQUEST_KANBAN_PROJECTS,
	RECEIVE_KANBAN_PROJECTS,
	RECEIVE_KANBAN_PROJECTS_ERROR,
	RECEIVE_PROJECTS,
	RECEIVE_PROJECTS_ERROR,
	REQUEST_ADD_PROJECT,
	RECEIVE_ADD_PROJECT,
	ADD_PROJECT_ERROR,
	REQUEST_DELETE_PROJECT,
	RECEIVE_DELETE_PROJECT,
	DELETE_PROJECT_ERROR,
	REQUEST_SAVE_PROJECT,
	RECEIVE_SAVE_PROJECT,
	SAVE_PROJECT_ERROR,
	ADD_LOCAL_PROJECT_UPDATE,
	DISCARD_PROJECT_UPDATES,
	RECEIVE_PROJECT,
	REQUEST_PROJECT,
	GET_PROJECT_ERROR,
} from '../actions/projectActionsRedux';

import { RECEIVE_ACTIVE_MODEL, RECEIVE_ADD_MODEL } from '../actions/activeModelActions';

/* All helper functions to handle project updates */
const updateProjectInfo = (projectToUpdate, projectUpdate) => {
	const updatedProject = { ...projectToUpdate };
	updatedProject.projectInfoFields = projectUpdate;
	return updatedProject;
};
const updateProjectStop = (projectToUpdate, projectUpdate) => {
	const updatedProject = { ...projectToUpdate };
	updatedProject.state.stop = projectUpdate;
	return updatedProject;
};
const updateProjectEscalation = (projectToUpdate, projectUpdate) => {
	const updatedProject = { ...projectToUpdate };
	updatedProject.state.escalated = projectUpdate;
	return updatedProject;
};
const updateQualityGateCompletion = (projectToUpdate, qualityGateGuid, projectUpdate) => {
	const updatedProject = { ...projectToUpdate };
	updatedProject.qualityGateValues[qualityGateGuid].completed = projectUpdate;
	return updatedProject;
};
const updateQualityGateClaim = (projectToUpdate, qualityGateGuid, projectUpdate) => {
	const updatedProject = { ...projectToUpdate };
	updatedProject.qualityGateValues[qualityGateGuid].claimed = projectUpdate;
	return updatedProject;
};
const updateQualityGateCheckpoint = (projectToUpdate, projectUpdate) => {
	const updatedProject = { ...projectToUpdate };
	const { qualityGateGuid, checkpoint: updatedCheckpoint } = projectUpdate;
	const { checkpointId } = updatedCheckpoint;

	const isVerifierKey = (key) =>
		updatedProject.qualityGateTableColumns.verifiers.some((verifier) => verifier.columnId === key);

	// Build the new checkpoint object out of the flat object we got from the frontend
	const newCheckpoint = { verifiers: {} };
	for (const [key, value] of Object.entries(updatedCheckpoint)) {
		if (!['checkpointId', 'checkpointDescription'].includes(key)) {
			// Split the key name by "_"
			const [key1, key2] = key.split('_');
			// NOTE: We could filter out the "function" === key2 case. But we dont since mongoose will and this simplifies the code
			if (!key2) {
				// If there is no second key we can just add the value to the checkpoint
				newCheckpoint[key1] = value;
			}
			// If there is a second key we have to create an object for it
			else if (isVerifierKey(key1)) {
				if (!newCheckpoint.verifiers[key1]) {
					newCheckpoint.verifiers[key1] = {};
				}

				if (key2 === 'userName') {
					if (!newCheckpoint.verifiers[key1].user) {
						newCheckpoint.verifiers[key1].user = {};
					}
					newCheckpoint.verifiers[key1].user.name = value;
				} else if (key2 === 'userId') {
					if (!newCheckpoint.verifiers[key1].user) {
						newCheckpoint.verifiers[key1].user = {};
					}
					newCheckpoint.verifiers[key1].user.id = value;
				} else {
					newCheckpoint.verifiers[key1][key2] = value;
				}
			} else {
				if (!newCheckpoint[key1]) {
					newCheckpoint[key1] = {};
				}

				if (key2 === 'userName') {
					if (!newCheckpoint[key1].user) {
						newCheckpoint[key1].user = {};
					}
					newCheckpoint[key1].user.name = value;
				} else if (key2 === 'userId') {
					if (!newCheckpoint[key1].user) {
						newCheckpoint[key1].user = {};
					}
					newCheckpoint[key1].user.id = value;
				} else {
					newCheckpoint[key1][key2] = value;
				}
			}
		}
	}

	// Update the checkpoint in the project
	updatedProject.qualityGateValues[qualityGateGuid].checkpoints[checkpointId] = newCheckpoint;

	return updatedProject;
};

/* All helper functions to store additional infos in redux state about each project */
const calcNotCompletedCheckpointsOfProject = (project) => {
	const newProject = { ...project };

	for (const verifier of newProject.qualityGateTableColumns.verifiers) {
		for (const activeQualityGateValues of Object.values(newProject.qualityGateValues)) {
			if (!activeQualityGateValues.notCompletedCheckpoints) {
				activeQualityGateValues.notCompletedCheckpoints = [];
			}

			const notCompletedCheckpoints = [];
			for (const checkpointId in activeQualityGateValues.checkpoints) {
				if (Object.hasOwn(activeQualityGateValues.checkpoints, checkpointId)) {
					const checkpoint = activeQualityGateValues.checkpoints[checkpointId];
					if (checkpoint.verifiers[verifier.columnId].completed === verifier.actionDefaultOption) {
						notCompletedCheckpoints.push(checkpointId);
					}
				}
			}
			activeQualityGateValues.notCompletedCheckpoints.push(...notCompletedCheckpoints);
		}
	}

	// Filter out duplicates
	for (const activeQualityGateValues of Object.values(newProject.qualityGateValues)) {
		activeQualityGateValues.notCompletedCheckpoints = [...new Set(activeQualityGateValues.notCompletedCheckpoints)];
	}

	return newProject;
};

const calcClaimedCheckpointsOfProject = (project) => {
	const newProject = { ...project };
	for (const activeQualityGateValues of Object.values(newProject.qualityGateValues)) {
		if (!activeQualityGateValues.claimedCheckpoints) {
			activeQualityGateValues.claimedCheckpoints = [];
		}

		const claimedCheckpoints = [];
		for (const checkpointId in activeQualityGateValues.checkpoints) {
			if (Object.hasOwn(activeQualityGateValues.checkpoints, checkpointId)) {
				const checkpoint = activeQualityGateValues.checkpoints[checkpointId];
				if (checkpoint.claimed?.value === 'true' && checkpoint.claimed.solved !== 'true') {
					claimedCheckpoints.push(checkpointId);
				}
			}
		}
		activeQualityGateValues.claimedCheckpoints.push(...claimedCheckpoints);
	}

	// Filter out duplicates
	for (const activeQualityGateValues of Object.values(newProject.qualityGateValues)) {
		activeQualityGateValues.claimedCheckpoints = [...new Set(activeQualityGateValues.claimedCheckpoints)];
	}

	return newProject;
};

const calcFlaggedCheckpointsOfProject = (project) => {
	const newProject = { ...project };
	for (const activeQualityGateValues of Object.values(newProject.qualityGateValues)) {
		if (!activeQualityGateValues.flaggedCheckpoints) {
			activeQualityGateValues.flaggedCheckpoints = [];
		}

		const flaggedCheckpoints = [];
		for (const checkpointId in activeQualityGateValues.checkpoints) {
			if (Object.hasOwn(activeQualityGateValues.checkpoints, checkpointId)) {
				const checkpoint = activeQualityGateValues.checkpoints[checkpointId];
				if (checkpoint.flag?.value === true) {
					flaggedCheckpoints.push(checkpointId);
				}
			}
		}
		activeQualityGateValues.flaggedCheckpoints.push(...flaggedCheckpoints);
	}

	// Filter out duplicates
	for (const activeQualityGateValues of Object.values(newProject.qualityGateValues)) {
		activeQualityGateValues.flaggedCheckpoints = [...new Set(activeQualityGateValues.flaggedCheckpoints)];
	}

	return newProject;
};

const calcVerifierStoplightActions = (project) => {
	const newProject = { ...project };

	for (const verifier of newProject.qualityGateTableColumns.verifiers) {
		const { actionStoplightOptions } = verifier;
		if (actionStoplightOptions?.length > 0) {
			for (const activeQualityGateValues of Object.values(newProject.qualityGateValues)) {
				if (!activeQualityGateValues.markedCheckpoints) {
					activeQualityGateValues.markedCheckpoints = [];
				}

				const markedCheckpoints = [];
				for (const checkpointId in activeQualityGateValues.checkpoints) {
					if (Object.hasOwn(activeQualityGateValues.checkpoints, checkpointId)) {
						const checkpoint = activeQualityGateValues.checkpoints[checkpointId];
						if (actionStoplightOptions.includes(checkpoint.verifiers[verifier.columnId].completed)) {
							markedCheckpoints.push(checkpointId);
						}
					}
				}
				activeQualityGateValues.markedCheckpoints.push(...markedCheckpoints);
			}
		}
	}

	// Filter out duplicates
	for (const activeQualityGateValues of Object.values(newProject.qualityGateValues)) {
		activeQualityGateValues.markedCheckpoints = [...new Set(activeQualityGateValues.markedCheckpoints)];
	}

	return newProject;
};

/**
 * Stores additional infos about the project in the redux state
 * @param {Object} project
 * @returns
 */
const calcAdditionalInfosOfProject = (project) =>
	// NOTE: This chaining of functions is not very performant. But it is only done once per project and it is very readable
	calcFlaggedCheckpointsOfProject(
		calcClaimedCheckpointsOfProject(calcNotCompletedCheckpointsOfProject(calcVerifierStoplightActions(project)))
	);

const initialProjectsState = {
	// here we store the fetching and error info for each setting
	isFetching: {},
	error: {},
	projects: {}, // All projects with local changes applied to them
	projectUpdates: {}, // All local changes to projects that have not been saved yet (not sent to the server)
	cachedProjects: {}, // The projects that were fetched from the server without any local changes
	kanbanProjects: {},
};

// eslint-disable-next-line import/prefer-default-export
export const projectsReducer = (state = initialProjectsState, action) => {
	switch (action.type) {
		// Done.
		case ADD_LOCAL_PROJECT_UPDATE: {
			/*
				ProjectUpdate = {
					action: "updateQualityGate" | "updateEditableTable" | "updateProjectInfo",
					id: String // id of the [editable table widget] or the [quality gate] or the [project info entry]
					updatedValue: Object // the new value
				}
			*/
			const { viewId, projectId, projectUpdate } = action.payload;

			/* Add project update to the list of updates for this project */
			const projectUpdates = state.projectUpdates[projectId] || [];
			// Check if this update (type & id) is already in the list
			const index = projectUpdates.findIndex(
				(update) => update.action === projectUpdate.action && update.id === projectUpdate.id
			);
			if (index >= 0) {
				// If it is, replace it
				projectUpdates[index] = projectUpdate;
			} else {
				// If not, add it
				projectUpdates.push(projectUpdate);
			}

			/* Apply the update to the stored project */
			const projects = state.projects[viewId];
			const projectIndex = projects.findIndex((project) => project._id === projectId);
			if (projectIndex >= 0) {
				let updatedProject;
				switch (projectUpdate.action) {
					case 'updateProjectInfo': {
						updatedProject = updateProjectInfo(projects[projectIndex], projectUpdate.updatedValue);
						break;
					}
					case 'updateProjectStop': {
						updatedProject = updateProjectStop(projects[projectIndex], projectUpdate.updatedValue);
						break;
					}
					case 'updateProjectEscalation': {
						updatedProject = updateProjectEscalation(projects[projectIndex], projectUpdate.updatedValue);
						break;
					}
					case 'updateQualityGateCompletion': {
						updatedProject = updateQualityGateCompletion(
							projects[projectIndex],
							projectUpdate.id,
							projectUpdate.updatedValue
						);
						break;
					}
					case 'claimQualityGate': {
						updatedProject = updateQualityGateClaim(
							projects[projectIndex],
							projectUpdate.id,
							projectUpdate.updatedValue
						);
						break;
					}
					case 'updateQualityGateCheckpoint': {
						updatedProject = updateQualityGateCheckpoint(projects[projectIndex], projectUpdate.updatedValue);
						break;
					}
					default: {
						// If the update is not handled, do nothing
						break;
					}
				}

				// Update the project in the list
				if (updatedProject) {
					projects[projectIndex] = calcAdditionalInfosOfProject(updatedProject);
				}

				return {
					...state,
					projectUpdates: { ...state.projectUpdates, [projectId]: projectUpdates },
					projects: { ...state.projects, [viewId]: projects },
				};
			}
			return state;
		}
		// Done.
		case DISCARD_PROJECT_UPDATES: {
			const { viewId, projectId } = action.payload;

			// Remove the project updates from the list of updates & reset the project to the cached version
			const projects = state.projects[viewId];
			const projectIndex = projects.findIndex((project) => project._id === projectId);
			if (projectIndex >= 0) {
				const cachedProject = state.cachedProjects[viewId].find((project) => project._id === projectId);
				projects[projectIndex] = calcAdditionalInfosOfProject(cachedProject);
			}

			return {
				...state,
				projectUpdates: { ...state.projectUpdates, [projectId]: [] },
				projects: { ...state.projects, [viewId]: projects },
			};
		}
		// Done.
		case REQUEST_PROJECTS: {
			return {
				...state,
				isFetching: { ...state.isFetching, [action.payload.viewId]: true },
				error: { ...state.error, [action.payload.viewId]: null },
			};
		}
		// Done.
		case RECEIVE_PROJECTS: {
			const { projects, viewId } = action.payload;

			const newProjects = projects.map((project) => calcAdditionalInfosOfProject(project));

			return {
				...state,
				isFetching: { ...state.isFetching, [viewId]: false },
				projects: { ...state.projects, [viewId]: newProjects },
				cachedProjects: { ...state.cachedProjects, [viewId]: projects },
			};
		}
		// Done.
		case RECEIVE_PROJECTS_ERROR: {
			return {
				...state,
				error: { ...state.error, [action.payload.viewId]: action.payload.error },
				isFetching: { ...state.isFetching, [action.payload.viewId]: false },
			};
		}
		// Done.
		case REQUEST_ADD_PROJECT: {
			return {
				...state,
				isFetching: { ...state.isFetching, [action.payload.viewId]: true },
				error: { ...state.error, [action.payload.viewId]: null },
			};
		}
		// Done.
		case RECEIVE_ADD_PROJECT: {
			const { project, viewId } = action.payload;

			return {
				...state,
				isFetching: { ...state.isFetching, [viewId]: false },
				projects: { ...state.projects, [viewId]: [...state.projects[viewId], project] },
				cachedProjects: { ...state.cachedProjects, [viewId]: [...state.projects[viewId], project] },
			};
		}
		// Done.
		case ADD_PROJECT_ERROR: {
			return {
				...state,
				error: { ...state.error, [action.payload.viewId]: action.payload.error },
				isFetching: { ...state.isFetching, [action.payload.viewId]: false },
			};
		}

		// Done.
		case REQUEST_SAVE_PROJECT: {
			return {
				...state,
				isFetching: { ...state.isFetching, [action.payload.viewId]: true },
				error: { ...state.error, [action.payload.viewId]: null },
			};
		}
		// Done.
		case RECEIVE_SAVE_PROJECT: {
			// update the changed project in store
			const updatedProject = action.payload;
			const { viewId } = action.payload;
			const projects = state.projects[viewId];
			const index = projects.findIndex((project) => project._id === updatedProject._id);
			projects[index] = calcAdditionalInfosOfProject(updatedProject);

			const cachedProjects = state.cachedProjects[viewId];
			const cachedIndex = cachedProjects.findIndex((project) => project._id === updatedProject._id);
			cachedProjects[cachedIndex] = updatedProject;

			return {
				...state,
				projects: { ...state.projects, [viewId]: projects },
				cachedProjects: { ...state.cachedProjects, [viewId]: cachedProjects },
			};
		}
		// Done.
		case SAVE_PROJECT_ERROR: {
			return {
				...state,
				error: { ...state.error, [action.payload.viewId]: action.payload.error },
				isFetching: { ...state.isFetching, [action.payload.viewId]: false },
			};
		}

		// Done.
		case REQUEST_PROJECT: {
			return {
				...state,
				isFetching: { ...state.isFetching, [action.payload.viewId]: true },
				error: { ...state.error, [action.payload.viewId]: null },
			};
		}
		// Done.
		case RECEIVE_PROJECT: {
			// update the changed project in store
			const updatedProject = action.payload;
			const { viewId } = action.payload;
			const projects = state.projects[viewId];
			const index = projects.findIndex((project) => project._id === updatedProject._id);
			projects[index] = calcAdditionalInfosOfProject(updatedProject);

			const cachedProjects = state.cachedProjects[viewId];
			const cachedIndex = cachedProjects.findIndex((project) => project._id === updatedProject._id);
			cachedProjects[cachedIndex] = updatedProject;

			return {
				...state,
				projects: { ...state.projects, [viewId]: projects },
				cachedProjects: { ...state.cachedProjects, [viewId]: cachedProjects },
			};
		}


		case REQUEST_KANBAN_PROJECTS: {
			return {
				...state,
				isFetching: { ...state.isFetching, [action.payload.viewId]: true },
				error: { ...state.error, [action.payload.viewId]: null },
			};
		}

		case RECEIVE_KANBAN_PROJECTS: {
			const { projects, viewId } = action.payload;

			return {
				...state,
				isFetching: { ...state.isFetching, [viewId]: false },
				kanbanProjects: { ...state.kanbanProjects, [viewId]: projects },
			};
		}

		case RECEIVE_KANBAN_PROJECTS_ERROR: {
			return {
				...state,
				error: { ...state.error, [action.payload.viewId]: action.payload.error },
				isFetching: { ...state.isFetching, [action.payload.viewId]: false },
			};
		}

		case GET_PROJECT_ERROR: {
			return {
				...state,
				error: { ...state.error, [action.payload.viewId]: action.payload.error },
				isFetching: { ...state.isFetching, [action.payload.viewId]: false },
			};
		}
		// Done.
		case REQUEST_DELETE_PROJECT:
			// keeps state, notifys that fetching started
			return {
				...state,
				isFetching: { ...state.isFetching, [action.payload.viewId]: true },
				error: { ...state.error, [action.payload.viewId]: null },
			};
		// Done.
		case RECEIVE_DELETE_PROJECT: {
			// removes project from state
			const { projectId, viewId } = action.payload;
			const newProjects = state.projects[viewId].filter((project) => project._id !== projectId);

			const newCachedProjects = state.cachedProjects[viewId].filter((project) => project._id !== projectId);

			return {
				...state,
				isFetching: { ...state.isFetching, [viewId]: false },
				projects: { ...state.projects, [viewId]: newProjects },
				cachedProjects: { ...state.cachedProjects, [viewId]: newCachedProjects },
			};
		}
		// Done.
		case DELETE_PROJECT_ERROR:
			return {
				...state,
				error: { ...state.error, [action.payload.viewId]: action.payload.error },
				isFetching: { ...state.isFetching, [action.payload.viewId]: false },
			};

		// Done.
		case RECEIVE_ACTIVE_MODEL:
			return initialProjectsState;
		// Done.
		case RECEIVE_ADD_MODEL:
			return initialProjectsState;
		default:
			return state;
	}
};
